import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { NoData } from '../../components/Illustrations/NoData';
import { MembershipCard } from '../../components/MembershipCard';
import {
  PATH_ORGANIZATION_MEMBERSHIPS,
  PATH_ORGANIZATION_MEMBERSHIPS_EDIT,
} from '../../app/constants/paths.constant';
import { RightDrawer } from '../../components/RightDrawer';
import { OrganizationForm } from '../../forms/ThirdParty/Organization';
import { SpeedDial } from '../../components/SpeedDial';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { FormVariant } from '../../forms';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const OrganizationPage_Memberships_Organization_Fragment = graphql(`
  fragment OrganizationPage_Memberships_Organization_Fragment on Organization {
    _id
    isInternal
    activeMemberships {
      personId
      ...OrganizationMembershipCardCmp_OrganizationMembership_Fragment
    }
    ...ThirdPartyForm_Organization_Fragment
  }
`);

interface Props {
  readonly organization: FragmentType<
    typeof OrganizationPage_Memberships_Organization_Fragment
  >;
}

export const Memberships = ({
  organization: pOrganization,
}: Readonly<Props>) => {
  const organization = getFragmentData(
    OrganizationPage_Memberships_Organization_Fragment,
    pOrganization,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_ORGANIZATION_MEMBERSHIPS,
      edit: PATH_ORGANIZATION_MEMBERSHIPS_EDIT,
    },
    { id: organization._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { activeMemberships } = organization;

  return (
    <>
      <Box sx={{ pt: 4, width: '100%' }}>
        {!activeMemberships.length ? (
          <NoData />
        ) : (
          <Grid container spacing={2}>
            {activeMemberships.map((activeMembership) => (
              <MembershipCard
                key={activeMembership.personId}
                organizationMembership={activeMembership}
              />
            ))}
          </Grid>
        )}
      </Box>
      <SpeedDial
        actions={[
          {
            id: 'editOrganizationMemberships',
            icon: <EditIcon />,
            name: 'Modifier Membres',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Membres de l'Organisation"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <OrganizationForm
          formVariant={FormVariant.Modification}
          initialValues={organization}
          isInternal={organization.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          organizationId={organization._id}
          stepIdsSubset={[
            ThirdPartyFormTab.OrganizationMemberships,
            ThirdPartyFormTab.OrganizationMembershipsDetails,
          ]}
          title="Formulaire de modification des Membres de l'Organisation"
        />
      </RightDrawer>
    </>
  );
};
